/**
 * Utility to compress images in the browser before uploading.
 * Reduces 5MB-10MB mobile photos down to ~200-400KB while preserving high visual quality.
 *
 * NOTE: Designed to be 100% fail-safe on all mobile browsers and webviews.
 * If canvas, FileReader, or Blob-to-File is unsupported or fails for any reason
 * (e.g. low memory, tainted canvas, timeout), it gracefully returns the original File untouched,
 * allowing the backend Pillow compressor to handle compression.
 */
export async function compressImage(
  file: File,
  maxDimension = 1600,
  quality = 0.82
): Promise<File> {
  try {
    // 1. Non-image files, SVGs, or GIFs are returned as-is
    if (!file || !file.type || !file.type.startsWith('image/') || file.type === 'image/svg+xml' || file.type === 'image/gif') {
      return file;
    }

    // 2. Check for required browser environment APIs
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof FileReader === 'undefined' ||
      !document.createElement
    ) {
      return file;
    }

    const canvas = document.createElement('canvas');
    if (!canvas || typeof canvas.getContext !== 'function') {
      return file;
    }

    // 3. Compression promise with a 2500ms safety timeout fallback
    return await new Promise<File>((resolve) => {
      let resolved = false;
      const safeResolve = (result: File) => {
        if (!resolved) {
          resolved = true;
          resolve(result);
        }
      };

      // 2.5s timeout: if image decoding hangs on mobile, fallback immediately
      const timeoutId = setTimeout(() => {
        safeResolve(file);
      }, 2500);

      try {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const dataUrl = e.target?.result;
            if (typeof dataUrl !== 'string') {
              clearTimeout(timeoutId);
              safeResolve(file);
              return;
            }

            const img = new Image();

            img.onload = () => {
              clearTimeout(timeoutId);
              try {
                let { width, height } = img;
                if (!width || !height) {
                  safeResolve(file);
                  return;
                }

                if (width > maxDimension || height > maxDimension) {
                  if (width > height) {
                    height = Math.round((height * maxDimension) / width);
                    width = maxDimension;
                  } else {
                    width = Math.round((width * maxDimension) / height);
                    height = maxDimension;
                  }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  safeResolve(file);
                  return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                // Check canvas.toBlob support
                if (typeof canvas.toBlob !== 'function') {
                  safeResolve(file);
                  return;
                }

                canvas.toBlob(
                  (blob) => {
                    try {
                      if (!blob || blob.size >= file.size) {
                        // Original file is already smaller or blob failed
                        safeResolve(file);
                        return;
                      }

                      const cleanName = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
                      // Try constructor: some older WebViews throw on `new File([blob])`
                      const compressedFile = new File([blob], cleanName, {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                      });
                      safeResolve(compressedFile);
                    } catch {
                      // Fallback: File constructor with blob failed on this device
                      safeResolve(file);
                    }
                  },
                  'image/jpeg',
                  quality
                );
              } catch {
                safeResolve(file);
              }
            };

            img.onerror = () => {
              clearTimeout(timeoutId);
              safeResolve(file);
            };

            img.src = dataUrl;
          } catch {
            clearTimeout(timeoutId);
            safeResolve(file);
          }
        };

        reader.onerror = () => {
          clearTimeout(timeoutId);
          safeResolve(file);
        };

        reader.readAsDataURL(file);
      } catch {
        clearTimeout(timeoutId);
        safeResolve(file);
      }
    });
  } catch {
    // Top-level catch: return untouched file
    return file;
  }
}
